import React, { useEffect, useState } from "react";
import { Plus, Trash2, Copy, Loader2, Plug } from "lucide-react";
import { toast } from "sonner";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";

const STATUS_CLS = {
    beta: "bg-brand-cyan/15 text-brand-cyan border-brand-cyan/30",
    planned: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
    connected: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export default function Integrations() {
    const { locale } = useI18n();
    const [cat, setCat] = useState([]);
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newKey, setNewKey] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [c, k] = await Promise.all([extrasApi.integrationsCatalog(), extrasApi.apiKeys.list()]);
            setCat(c.integrations);
            setKeys(k);
        } catch (_e) { toast.error("Failed"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function createKey() {
        const name = window.prompt("Key name (e.g. 'Zapier prod'):");
        if (!name) return;
        setCreating(true);
        try {
            const res = await extrasApi.apiKeys.create(name);
            setNewKey(res);
            load();
        } catch (_e) { toast.error("Failed"); }
        finally { setCreating(false); }
    }

    async function deleteKey(id) {
        if (!window.confirm("Delete this API key?")) return;
        try {
            await extrasApi.apiKeys.remove(id);
            toast.success("Deleted");
            load();
        } catch (_e) { toast.error("Failed"); }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{bl("Integrations", "Intégrations", locale)}</h1>
                <p className="mt-1 text-sm text-zinc-500">
                    {bl("Connect your CRM, email, Slack and automation stack — or use our REST API.",
                        "Connecte ton CRM, email, Slack et ta stack d'automatisation — ou utilise notre API REST.", locale)}
                </p>
            </div>

            <div className="mb-8">
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{bl("Catalog", "Catalogue", locale)}</div>
                {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {cat.map((it) => (
                            <div key={it.key} className="rounded-2xl p-5 bg-brand-surface border border-white/8 hover:border-white/20 transition" data-testid={`integration-${it.key}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className="w-10 h-10 rounded-lg bg-brand-void border border-white/8 flex items-center justify-center text-brand-cyan">
                                        <Plug size={16} />
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_CLS[it.status] || STATUS_CLS.planned}`}>
                                        {it.status}
                                    </span>
                                </div>
                                <div className="text-white font-semibold">{it.name}</div>
                                <div className="text-xs text-zinc-500 mt-1">{it.category}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">{bl("API keys", "Clés API", locale)}</div>
                    <button type="button" onClick={createKey} disabled={creating} data-testid="api-key-create" className="btn-cta text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2">
                        <Plus size={13} /> {bl("New key", "Nouvelle clé", locale)}
                    </button>
                </div>
                {newKey && (
                    <div className="mb-3 rounded-xl p-4 bg-brand-cyan/10 border border-brand-cyan/30" data-testid="api-key-created">
                        <div className="text-sm text-brand-cyan mb-2">{bl("Save this secret now — you won't see it again.", "Copie ce secret maintenant — tu ne le reverras pas.", locale)}</div>
                        <div className="flex items-center gap-2 bg-brand-void rounded-lg px-3 py-2 font-mono text-xs break-all">
                            <span className="flex-1">{newKey.secret}</span>
                            <button onClick={() => { navigator.clipboard.writeText(newKey.secret); toast.success("Copied"); }} className="text-brand-cyan hover:text-white"><Copy size={13} /></button>
                        </div>
                        <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-zinc-500 hover:text-white">Dismiss</button>
                    </div>
                )}
                <div className="rounded-2xl bg-brand-surface border border-white/8 overflow-hidden">
                    {keys.length === 0 ? (
                        <div className="p-10 text-center text-sm text-zinc-500">{bl("No API keys yet.", "Aucune clé API.", locale)}</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase text-zinc-500 border-b border-white/8"><tr>
                                <th className="p-3 text-left">Name</th><th className="p-3 text-left">Prefix</th><th className="p-3 text-left">Created</th><th className="p-3 w-10"></th>
                            </tr></thead>
                            <tbody>
                                {keys.map(k => (
                                    <tr key={k.key_id} className="border-b border-white/5" data-testid={`api-key-row-${k.key_id}`}>
                                        <td className="p-3 text-white">{k.name}</td>
                                        <td className="p-3 font-mono text-xs text-zinc-400">{k.prefix}</td>
                                        <td className="p-3 text-xs text-zinc-500 font-mono">{new Date(k.created_at).toLocaleString()}</td>
                                        <td className="p-3"><button onClick={() => deleteKey(k.key_id)} className="text-zinc-500 hover:text-rose-300"><Trash2 size={14} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
